import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, Settings, Clock } from "lucide-react";
import Link from "next/link";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useForm, SubmitHandler } from "react-hook-form";

import { useUpdateLeadStatus } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import { LeadStatus } from "@/app/_module/constants/leadStatus";
import Input from "@/app/_module/components/FormInput";
import { Button } from "@/app/_module/components/ui/button";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_module/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { toast } from "@/app/_module/hooks/use-toast";
import { cn } from "@/app/_module/lib/utils";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";

import { useCreateAppointment, useUpdateAppointment, useGetAppointments } from "../hooks/queries";
import type {
  CreateAppointment,
  UpdateAppointment,
} from "../schema/appointment-schema";
import { createAppointmentSchema } from "../schema/appointment-schema";
import type { Appointment } from "../types/appointments";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { LeadSelect } from "./LeadSelect";
import { findConflicts, formatMinutes, startMinutes, durationMinutes } from "../utils/schedule";

// Helper to check if two appointment times are within 5 minutes of each other
const isWithinFiveMinutes = (time1: Date, time2: Date): boolean => {
  const diffMs = Math.abs(time1.getTime() - time2.getTime());
  const fiveMinutesMs = 5 * 60 * 1000;
  return diffMs < fiveMinutesMs;
};

// State to timezone mapping (IANA timezone strings)
const stateTimezones: Record<string, { timezone: string; label: string }> = {
  AL: { timezone: "America/Chicago", label: "Central" },
  AK: { timezone: "America/Anchorage", label: "Alaska" },
  AZ: { timezone: "America/Phoenix", label: "Mountain (no DST)" },
  AR: { timezone: "America/Chicago", label: "Central" },
  CA: { timezone: "America/Los_Angeles", label: "Pacific" },
  CO: { timezone: "America/Denver", label: "Mountain" },
  CT: { timezone: "America/New_York", label: "Eastern" },
  DE: { timezone: "America/New_York", label: "Eastern" },
  FL: { timezone: "America/New_York", label: "Eastern" },
  GA: { timezone: "America/New_York", label: "Eastern" },
  HI: { timezone: "Pacific/Honolulu", label: "Hawaii" },
  ID: { timezone: "America/Boise", label: "Mountain" },
  IL: { timezone: "America/Chicago", label: "Central" },
  IN: { timezone: "America/Indiana/Indianapolis", label: "Eastern" },
  IA: { timezone: "America/Chicago", label: "Central" },
  KS: { timezone: "America/Chicago", label: "Central" },
  KY: { timezone: "America/New_York", label: "Eastern" },
  LA: { timezone: "America/Chicago", label: "Central" },
  ME: { timezone: "America/New_York", label: "Eastern" },
  MD: { timezone: "America/New_York", label: "Eastern" },
  MA: { timezone: "America/New_York", label: "Eastern" },
  MI: { timezone: "America/Detroit", label: "Eastern" },
  MN: { timezone: "America/Chicago", label: "Central" },
  MS: { timezone: "America/Chicago", label: "Central" },
  MO: { timezone: "America/Chicago", label: "Central" },
  MT: { timezone: "America/Denver", label: "Mountain" },
  NE: { timezone: "America/Chicago", label: "Central" },
  NV: { timezone: "America/Los_Angeles", label: "Pacific" },
  NH: { timezone: "America/New_York", label: "Eastern" },
  NJ: { timezone: "America/New_York", label: "Eastern" },
  NM: { timezone: "America/Denver", label: "Mountain" },
  NY: { timezone: "America/New_York", label: "Eastern" },
  NC: { timezone: "America/New_York", label: "Eastern" },
  ND: { timezone: "America/Chicago", label: "Central" },
  OH: { timezone: "America/New_York", label: "Eastern" },
  OK: { timezone: "America/Chicago", label: "Central" },
  OR: { timezone: "America/Los_Angeles", label: "Pacific" },
  PA: { timezone: "America/New_York", label: "Eastern" },
  RI: { timezone: "America/New_York", label: "Eastern" },
  SC: { timezone: "America/New_York", label: "Eastern" },
  SD: { timezone: "America/Chicago", label: "Central" },
  TN: { timezone: "America/Chicago", label: "Central" },
  TX: { timezone: "America/Chicago", label: "Central" },
  UT: { timezone: "America/Denver", label: "Mountain" },
  VT: { timezone: "America/New_York", label: "Eastern" },
  VA: { timezone: "America/New_York", label: "Eastern" },
  WA: { timezone: "America/Los_Angeles", label: "Pacific" },
  WV: { timezone: "America/New_York", label: "Eastern" },
  WI: { timezone: "America/Chicago", label: "Central" },
  WY: { timezone: "America/Denver", label: "Mountain" },
  DC: { timezone: "America/New_York", label: "Eastern" },
  PR: { timezone: "America/Puerto_Rico", label: "Atlantic" },
};

interface NewAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (appointment: CreateAppointment | UpdateAppointment) => void; // Optional, for parent callback
  onStatusChange?: (leadId: string, newStatus: string) => void; // Optional callback when lead status is updated (for tracking)
  editingAppointment?: Appointment; // Add this prop for editing
  preselectedLeadId?: string; // Auto-select lead when opened from lead page
  preselectedLeadName?: string; // Auto-select lead name when opened from lead page
  preselectedLeadState?: string; // Lead's state for timezone display
}

export const NewAppointmentDialog = function ({
  open,
  onOpenChange,
  onSave,
  onStatusChange,
  editingAppointment,
  preselectedLeadId,
  preselectedLeadName,
  preselectedLeadState,
}: NewAppointmentDialogProps) {
  const isEditing = !!editingAppointment;
  const [selectedLeadId, setSelectedLeadId] = useState<string>("");
  const [selectedLeadState, setSelectedLeadState] = useState<string | undefined>(preselectedLeadState);
  const [hasEmailConnected, setHasEmailConnected] = useState<boolean>(false);
  const [isCheckingEmailConnection, setIsCheckingEmailConnection] = useState<boolean>(true);
  const timeListRef = useRef<HTMLDivElement>(null);

  // Get current time rounded to next 15-minute slot
  const getDefaultTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    // Handle overflow to next hour
    const adjustedHours = minutes === 60 ? hours + 1 : hours;
    const adjustedMinutes = minutes === 60 ? 0 : minutes;
    // Handle overflow to next day (just use 23:45 if past midnight)
    const finalHours = adjustedHours >= 24 ? 23 : adjustedHours;
    const finalMinutes = adjustedHours >= 24 ? 45 : adjustedMinutes;
    return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
  };

  const form = useForm<CreateAppointment>({
    resolver: zodResolver(createAppointmentSchema),
    mode: "onChange",
    defaultValues: {
      title: "",
      time: getDefaultTime(),
      apptDate: format(new Date(), "yyyy-MM-dd"),
      apptNotes: "",
      priorityStatus: "medium" as const,
      sendEmailConf: false,
      associations: [],
    },
  });

  const {
    control,
    formState: { isValid },
    reset,
    handleSubmit,
    setValue,
  } = form;

  const createAppointmentMutation = useCreateAppointment();
  const updateAppointmentMutation = useUpdateAppointment();
  const updateLeadStatusMutation = useUpdateLeadStatus();

  // Fetch existing appointments to check for duplicates
  const { data: existingAppointments } = useGetAppointments({
    page: 1,
    limit: 100, // Fetch enough to check for duplicates
  });

  // Default values for form reset
  const getDefaultValues = () => ({
    title: "",
    time: getDefaultTime(),
    apptDate: format(new Date(), "yyyy-MM-dd"),
    apptNotes: "",
    duration: 30,
    priorityStatus: "medium" as const,
    sendEmailConf: false,
    associations: [] as CreateAppointment["associations"],
  });

  // Check if user has connected email (Gmail/Outlook) when dialog opens
  useEffect(() => {
    const checkEmailConnection = async () => {
      if (!open) return;

      setIsCheckingEmailConnection(true);
      try {
        const isConnected = await oauthConnectionService.hasOAuthConnected();
        setHasEmailConnected(isConnected);
      } catch (error) {
        console.error("Failed to check email connection status:", error);
        setHasEmailConnected(false);
      } finally {
        setIsCheckingEmailConnection(false);
      }
    };

    checkEmailConnection();
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      return;
    }

    if (editingAppointment) {
      setValue("title", editingAppointment.title || "");
      // Ensure time is in HH:MM format (strip seconds if present)
      const rawTime = editingAppointment.time || "09:00";
      const timeParts = rawTime.split(":");
      setValue("time", `${timeParts[0]}:${timeParts[1]}`);
      // Ensure date is in YYYY-MM-DD format for validation
      let dateStr = editingAppointment.date || format(new Date(), "yyyy-MM-dd");
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }
      setValue("apptDate", dateStr);
      setValue("apptNotes", editingAppointment.notes || "");
      // Duration was missing here, so editing any appointment silently reset it
      // to the 30-minute default on save.
      const existingDuration = Number(
        (editingAppointment as { duration?: number }).duration,
      );
      setValue(
        "duration" as never,
        (Number.isFinite(existingDuration) && existingDuration > 0
          ? existingDuration
          : 30) as never,
      );
      setValue("associations", editingAppointment.associations || []);
      setValue("status", (editingAppointment.status || "scheduled") as "pending" | "scheduled" | "confirmed" | "cancelled" | "completed" | "no_show" | "overdue");
      setValue("priorityStatus", (editingAppointment.priorityStatus || "medium") as "low" | "medium" | "high" | "urgent");

      // Set selected lead ID for editing mode
      const leadAssociation = editingAppointment.associations?.find(
        (assoc) => assoc.entityType === "Lead"
      );
      if (leadAssociation) {
        setSelectedLeadId(leadAssociation.entityId);
      }
    } else if (preselectedLeadId) {
      // Handle preselected lead when opened from lead page
      // First reset form to defaults
      reset(getDefaultValues());
      setSelectedLeadId(preselectedLeadId);
      setSelectedLeadState(preselectedLeadState);
      // Auto-fill title with lead name for convenience
      if (preselectedLeadName) {
        setValue("title", `Appointment with ${preselectedLeadName}`);
      }
      setValue("associations", [
        {
          entityType: "Lead",
          entityId: preselectedLeadId,
          entityName: preselectedLeadName,
          notes: "Primary contact for this appointment",
        },
      ]);
    } else {
      // New appointment without preselected lead - reset to defaults
      reset(getDefaultValues());
      setSelectedLeadId("");
    }
  }, [open, editingAppointment, preselectedLeadId, preselectedLeadName, setValue, reset]);

  const handleLeadSelect = (leadId: string, leadName?: string, leadState?: string) => {
    setSelectedLeadId(leadId);
    setSelectedLeadState(leadState);
    setValue("associations", [
      {
        entityType: "Lead",
        entityId: leadId,
        entityName: leadName,
        notes: "Primary contact for this appointment",
      },
    ]);
  };

  const onSubmit: SubmitHandler<CreateAppointment> = async (data) => {
    // Check for duplicate appointments within 5 minutes of the new appointment time
    if (!isEditing) {
      const newAppointmentDate = new Date(`${data.apptDate}T${data.time}:00`);
      const appointments = existingAppointments?.items || [];

      const duplicateAppointment = appointments.find((apt) => {
        if (!apt.startTime) return false;
        const existingDate = new Date(apt.startTime);
        return isWithinFiveMinutes(newAppointmentDate, existingDate);
      });

      if (duplicateAppointment) {
        const existingTime = new Date(duplicateAppointment.startTime!).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        toast({
          title: "Duplicate appointment",
          description: `You already have an appointment "${duplicateAppointment.title}" scheduled at ${existingTime}. Please choose a time at least 5 minutes apart.`,
          variant: "destructive",
        });
        return;
      }
    }

    if (isEditing) {
      updateAppointmentMutation.mutate(
        {
          appointmentId: editingAppointment.id,
          data: data as UpdateAppointment,
        },
        {
          onSuccess: () => {
            toast({
              title: "Appointment updated",
              description: "The appointment has been updated successfully.",
            });

            // Email confirmation is now handled by the backend
            if (onSave) onSave(data);
            onOpenChange(false);
            setSelectedLeadId("");
            reset(getDefaultValues());
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            toast({
              title: "Couldn't update appointment",
              description:
                error?.message || "Something went wrong. Please try again.",
              variant: "destructive",
            });

          },
        }
      );
    } else {
      createAppointmentMutation.mutate(data, {
        onSuccess: async () => {
          toast({
            title: "Appointment created",
            description: "The appointment has been scheduled successfully.",
          });

          // Email confirmation is now handled by the backend

          // Automatically update lead status to "Appt" when appointment is created
          const leadIdToUpdate = selectedLeadId || preselectedLeadId;
          if (leadIdToUpdate) {
            try {
              await updateLeadStatusMutation.mutateAsync({
                leadId: leadIdToUpdate,
                status: LeadStatus.APPOINTMENT,
              });
              // Notify parent of status change (for tracking in session stats)
              if (onStatusChange) {
                onStatusChange(leadIdToUpdate, LeadStatus.APPOINTMENT);
              }
            } catch (error) {
              // Error is already handled in the mutation's onError

            }
          }

          if (onSave) onSave(data);
          onOpenChange(false);
          setSelectedLeadId("");
          reset(getDefaultValues());
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          toast({
            title: "Couldn't create appointment",
            description:
              error?.message || "Something went wrong. Please try again.",
            variant: "destructive",
          });

        },
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedLeadId("");
    reset(getDefaultValues());
  };

  const isSubmitting = isEditing
    ? updateAppointmentMutation.isPending
    : createAppointmentMutation.isPending;
  const submitButtonText = isSubmitting
    ? isEditing
      ? "Updating..."
      : "Creating..."
    : isEditing
      ? "Update Appointment"
      : "Create Appointment";

  // Get the effective lead state (from selection or preselected)
  const effectiveLeadState = selectedLeadState || preselectedLeadState;

  // Get today's date in the AGENT's timezone (time slots are in agent's local time)
  const getAgentToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get current time in the AGENT's timezone (for disabling past time slots)
  const getAgentCurrentTime = () => {
    const now = new Date();
    return { hours: now.getHours(), minutes: now.getMinutes() };
  };

  // Watch the date field to determine if we need time validation
  const selectedDate = form.watch('apptDate');

  /**
   * Overlap check against the day's existing appointments.
   *
   * The pre-existing guard only fired on submit and only caught starts within
   * 5 minutes of each other — booking 2:00 on top of a 2:00–2:45 was fine as
   * long as you picked 2:10. This compares real ranges (start + duration) and
   * surfaces the clash while you are still choosing the time.
   */
  const watchedTime = form.watch('time');
  const watchedDuration = Number((form.watch as (n: string) => unknown)('duration')) || 30;
  // "Custom end time" mode. Held as local UI state so the form still stores a
  // single `duration` — nothing downstream has to understand two competing
  // representations of the same thing.
  const [customEnd, setCustomEnd] = useState<string>("");
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const conflicts = useMemo(() => {
    if (!selectedDate || !watchedTime) return [];
    return findConflicts(
      (existingAppointments?.items || []) as never,
      selectedDate,
      watchedTime,
      watchedDuration,
      isEditing ? (editingAppointment as { id?: string } | undefined)?.id : undefined,
    );
  }, [existingAppointments, selectedDate, watchedTime, watchedDuration, isEditing, editingAppointment]);

  /** "today" / "tomorrow" / "on Tue, Aug 12" — "that day" reads oddly when the
   *  day in question is today. */
  const bookedDayLabel = useMemo(() => {
    if (!selectedDate) return "that day";
    const today = getAgentToday();
    if (selectedDate === today) return "today";
    const [ty, tm, td] = today.split("-").map(Number);
    const tomorrow = new Date(ty, tm - 1, td + 1);
    const tomorrowKey = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
    if (selectedDate === tomorrowKey) return "tomorrow";
    const [y, m, d] = selectedDate.split("-").map(Number);
    return `on ${new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })}`;
  }, [selectedDate]);

  /** Times already taken on the chosen day — shown under the time picker. */
  const bookedRanges = useMemo(() => {
    if (!selectedDate) return [] as Array<{ id: string; label: string; title: string }>;
    return ((existingAppointments?.items || []) as never as Array<Record<string, unknown>>)
      .filter((a) => {
        const d = typeof a.date === 'string' ? (a.date as string).split('T')[0] : String(a.date ?? '');
        const st = String(a.status || '').toLowerCase();
        return d === selectedDate && !['cancelled', 'no_show', 'completed'].includes(st);
      })
      .map((a) => {
        const st = startMinutes(a as never);
        if (st === null) return null;
        const mins = durationMinutes(a as never);
        return {
          id: String(a.id),
          title: String(a.title || 'Appointment'),
          label: `${formatMinutes(st)} – ${formatMinutes(st + mins)}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a!.label).localeCompare(String(b!.label))) as Array<{ id: string; label: string; title: string }>;
  }, [existingAppointments, selectedDate]);

  // Check if client has different timezone
  const hasTimezonesDiff = effectiveLeadState && stateTimezones[effectiveLeadState] &&
    stateTimezones[effectiveLeadState].timezone !== Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Generate time slots (every 15 minutes) for the dropdown
  const generateTimeSlots = () => {
    const slots: { value: string; label: string; clientLabel?: string; disabled: boolean }[] = [];
    const agentTime = getAgentCurrentTime();
    const isTodaySelected = selectedDate === getAgentToday();
    const agentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const clientTzInfo = effectiveLeadState ? stateTimezones[effectiveLeadState] : null;

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = String(hour).padStart(2, '0');
        const minStr = String(minute).padStart(2, '0');
        const value = `${hourStr}:${minStr}`;

        // Format label as 12-hour time (agent's time)
        const hour12 = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const label = `${hour12}:${minStr.padStart(2, '0')} ${ampm}`;

        // Calculate client's time if different timezone
        let clientLabel: string | undefined;
        if (clientTzInfo && clientTzInfo.timezone !== agentTimezone && selectedDate) {
          try {
            // Create date in agent's timezone
            const tempDate = new Date(`${selectedDate}T${value}:00`);
            // Format in client's timezone
            clientLabel = tempDate.toLocaleTimeString("en-US", {
              timeZone: clientTzInfo.timezone,
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
          } catch {
            // ignore
          }
        }

        // Disable past times when today is selected (in agent's timezone)
        let disabled = false;
        if (isTodaySelected) {
          if (hour < agentTime.hours || (hour === agentTime.hours && minute <= agentTime.minutes)) {
            disabled = true;
          }
        }

        slots.push({ value, label, clientLabel, disabled });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get the nearest 15-minute time slot value for current time
  const getCurrentTimeSlotValue = useCallback(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.floor(now.getMinutes() / 15) * 15;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }, []);

  // Scroll to current time slot when popover opens
  const scrollToCurrentTime = useCallback(() => {
    setTimeout(() => {
      if (timeListRef.current) {
        const currentSlotValue = getCurrentTimeSlotValue();
        // Find the button with the current time value using data attribute
        const targetButton = timeListRef.current.querySelector(`[data-time="${currentSlotValue}"]`);

        if (targetButton) {
          targetButton.scrollIntoView({ block: 'center', behavior: 'auto' });
        } else {
          // Fallback: find first non-disabled button
          const firstEnabledButton = timeListRef.current.querySelector('button:not([disabled])');
          if (firstEnabledButton) {
            firstEnabledButton.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
        }
      }
    }, 50); // Small delay to ensure DOM is ready
  }, [getCurrentTimeSlotValue]);

  // Format time value to 12-hour display
  const formatTimeDisplay = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const hour12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  // Convert selected time to client's timezone for display
  const getClientTimeDisplay = (time: string, date: string) => {
    if (!time || !date || !effectiveLeadState) return null;
    const tzInfo = stateTimezones[effectiveLeadState];
    if (!tzInfo) return null;

    try {
      // Create a date in the agent's local timezone
      const [hours, minutes] = time.split(':').map(Number);
      const localDate = new Date(`${date}T${time}:00`);

      // Get the agent's timezone
      const agentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Format time in client's timezone
      const clientTimeStr = localDate.toLocaleTimeString("en-US", {
        timeZone: tzInfo.timezone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      // Format time in agent's timezone
      const agentTimeStr = localDate.toLocaleTimeString("en-US", {
        timeZone: agentTimezone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      // Get timezone abbreviations
      const clientTzAbbr = tzInfo.label;
      const agentTzAbbr = new Intl.DateTimeFormat('en-US', {
        timeZone: agentTimezone,
        timeZoneName: 'short'
      }).formatToParts(localDate).find(p => p.type === 'timeZoneName')?.value || 'Local';

      return {
        clientTime: clientTimeStr,
        clientTz: clientTzAbbr,
        agentTime: agentTimeStr,
        agentTz: agentTzAbbr,
        sameTimezone: tzInfo.timezone === agentTimezone
      };
    } catch {
      return null;
    }
  };

  return (
    <React.Fragment>
    {!!open && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" />}

    <Dialog modal={false} onOpenChange={onOpenChange} open={open}>
      <ResponsiveDialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Appointment" : "New Appointment"}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? "Update appointment details below" : "Create a new appointment by filling out the form below"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <FormField
              control={control}
              name="title"
              render={({ field, fieldState }) => (
                <Input
                  required
                  error={fieldState.error}
                  label="Title"
                  placeholder="Enter appointment title"
                  {...field}
                />
              )}
            />

            {/* Date · Time · Duration on one row — 3 columns now that duration
                is a field, otherwise it wrapped onto a line of its own. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              <FormField
                control={control}
                name="apptDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date *</FormLabel>
                    <Popover>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="date"
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="w-full pr-10"
                            min={getAgentToday()}
                          />
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            >
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                            </Button>
                          </PopoverTrigger>
                        </div>
                      </FormControl>
                      <PopoverContent align="start" className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                          onSelect={(date) => {
                            if (date) {
                              // Format date in local timezone to avoid timezone shifts
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              field.onChange(`${year}-${month}-${day}`);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="time"
                render={({ field }) => {
                  // Check if current time value is in the past (for visual warning)
                  const isPastTime = () => {
                    if (selectedDate !== getAgentToday()) return false;
                    if (!field.value) return false;
                    const [hours, minutes] = field.value.split(':').map(Number);
                    const clientTime = getAgentCurrentTime();
                    return hours < clientTime.hours || (hours === clientTime.hours && minutes <= clientTime.minutes);
                  };

                  return (
                    <FormItem>
                      <FormLabel>Time *</FormLabel>
                      <Popover onOpenChange={(isOpen) => isOpen && scrollToCurrentTime()}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                                isPastTime() && "border-amber-400 bg-amber-50"
                              )}
                            >
                              {field.value ? formatTimeDisplay(field.value) : "Select time"}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className={cn("p-0", hasTimezonesDiff ? "w-[320px]" : "w-[280px]")} align="start">
                          {/* Manual time input at top */}
                          <div className="p-3 border-b">
                            <p className="text-xs text-muted-foreground mb-2">Type custom time{hasTimezonesDiff ? " (your time)" : ""}:</p>
                            <Input
                              type="time"
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              className="w-full"
                            />
                          </div>
                          {/* Quick select 15-min intervals */}
                          <div className="p-2">
                            {hasTimezonesDiff ? (
                              <>
                                <div className="flex justify-between text-xs text-muted-foreground mb-2 px-3">
                                  <span>Your Time</span>
                                  <span>Client ({stateTimezones[effectiveLeadState!].label})</span>
                                </div>
                                <div ref={timeListRef} className="flex flex-col gap-0.5 max-h-[250px] overflow-y-auto">
                                  {timeSlots.map((slot) => (
                                    <Button
                                      key={slot.value}
                                      type="button"
                                      data-time={slot.value}
                                      variant={field.value === slot.value ? "default" : "ghost"}
                                      size="sm"
                                      disabled={slot.disabled}
                                      className={cn(
                                        "text-sm h-8 justify-between px-3",
                                        slot.disabled && "text-muted-foreground line-through opacity-50",
                                        field.value === slot.value && "bg-primary text-primary-foreground"
                                      )}
                                      onClick={() => field.onChange(slot.value)}
                                    >
                                      <span>{slot.label}</span>
                                      <span className={cn(
                                        "text-muted-foreground",
                                        field.value === slot.value && "text-primary-foreground/70"
                                      )}>→ {slot.clientLabel}</span>
                                    </Button>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-muted-foreground mb-2 px-1">Or select:</p>
                                <div ref={timeListRef} className="grid grid-cols-4 gap-1 max-h-[200px] overflow-y-auto">
                                  {timeSlots.map((slot) => (
                                    <Button
                                      key={slot.value}
                                      type="button"
                                      data-time={slot.value}
                                      variant={field.value === slot.value ? "default" : "ghost"}
                                      size="sm"
                                      disabled={slot.disabled}
                                      className={cn(
                                        "text-xs h-8",
                                        slot.disabled && "text-muted-foreground line-through opacity-50",
                                        field.value === slot.value && "bg-primary text-primary-foreground"
                                      )}
                                      onClick={() => field.onChange(slot.value)}
                                    >
                                      {slot.label}
                                    </Button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      {isPastTime() && (
                        <p className="text-xs text-amber-600 mt-1">
                          This time has already passed today
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={control}
                name={"duration" as never}
                render={({ field }) => {
                  // End time is derived rather than entered — two free-text
                  // times invite end-before-start, and the backend stores a
                  // duration anyway.
                  const mins = Number(field.value) || 30;
                  const endLabel = (() => {
                    const t = form.watch("time");
                    if (!t) return null;
                    const [h, m] = String(t).split(":").map(Number);
                    if (Number.isNaN(h)) return null;
                    return formatMinutes(h * 60 + (Number.isNaN(m) ? 0 : m) + mins);
                  })();
                  return (
                    <FormItem>
                      <FormLabel>Duration</FormLabel>
                      <Select
                        value={isCustomDuration ? "custom" : String(mins)}
                        onValueChange={(v: string) => {
                          if (v === "custom") {
                            setIsCustomDuration(true);
                            return;
                          }
                          setIsCustomDuration(false);
                          setCustomEnd("");
                          field.onChange(Number(v));
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="30 minutes" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[15, 30, 45, 60, 90, 120].map((m) => (
                            <SelectItem key={m} value={String(m)}>
                              {m >= 60 ? `${m / 60} hr${m > 60 ? "s" : ""}` : `${m} minutes`}
                            </SelectItem>
                          ))}
                          <SelectItem value="custom">Custom end time…</SelectItem>
                        </SelectContent>
                      </Select>

                      {isCustomDuration ? (
                        (() => {
                          const startRaw = form.watch("time");
                          const [sh, sm] = String(startRaw || "").split(":").map(Number);
                          const startMin = Number.isNaN(sh) ? null : sh * 60 + (Number.isNaN(sm) ? 0 : sm);

                          if (startMin === null) {
                            return (
                              <p className="mt-2 text-xs text-muted-foreground">
                                Pick a start time first.
                              </p>
                            );
                          }

                          // End times are GENERATED from the start rather than
                          // typed. A raw time input made the user resolve AM/PM
                          // themselves — "3:15" after a 2pm start is ambiguous,
                          // and picking AM silently produced a negative
                          // duration. Every option here is after the start by
                          // construction, in 15-min steps up to 8 hours or the
                          // end of the day, whichever comes first.
                          const options: Array<{ value: string; label: string; mins: number }> = [];
                          for (let m = startMin + 15; m <= Math.min(startMin + 480, 23 * 60 + 45); m += 15) {
                            const mins = m - startMin;
                            const h = Math.floor(m / 60);
                            const mm = m % 60;
                            options.push({
                              value: `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
                              label: formatMinutes(m),
                              mins,
                            });
                          }

                          const humanLen = (n: number) =>
                            n >= 60
                              ? `${Math.floor(n / 60)}h${n % 60 ? ` ${n % 60}m` : ""}`
                              : `${n}m`;

                          return (
                            <div className="mt-2">
                              <Select
                                value={customEnd}
                                onValueChange={(v: string) => {
                                  setCustomEnd(v);
                                  const opt = options.find((o) => o.value === v);
                                  if (opt) field.onChange(opt.mins);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select end time" />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                  {options.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      <span className="flex w-full items-center justify-between gap-4">
                                        <span>{o.label}</span>
                                        <span className="text-xs text-muted-foreground">
                                          {humanLen(o.mins)}
                                        </span>
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {formatMinutes(startMin)} → {customEnd
                                  ? `${formatMinutes(startMin + mins)} · ${humanLen(mins)}`
                                  : "select an end time"}
                              </p>
                            </div>
                          );
                        })()
                      ) : (
                        !!endLabel && (
                          <p className="mt-1 text-xs text-muted-foreground">Ends at {endLabel}</p>
                        )
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            {/* Booking conflict — shown live while picking, not only on submit. */}
            {conflicts.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-amber-900">
                    That time is already booked
                  </p>
                  <ul className="mt-1 space-y-0.5 text-amber-800">
                    {conflicts.slice(0, 4).map((c) => {
                      const st = startMinutes(c);
                      const mins = durationMinutes(c);
                      return (
                        <li key={c.id} className="text-xs">
                          {st !== null
                            ? `${formatMinutes(st)} – ${formatMinutes(st + mins)}`
                            : "No time set"}{" "}
                          · {c.title || "Appointment"}
                        </li>
                      );
                    })}
                    {conflicts.length > 4 && (
                      <li className="text-xs opacity-80">+{conflicts.length - 4} more</li>
                    )}
                  </ul>
                  <p className="mt-1 text-xs text-amber-700">
                    You can still book it — double-booking is allowed, just flagged.
                  </p>
                </div>
              </div>
            )}

            {/* What's already on the books that day, so a free slot is easy to find. */}
            {selectedDate && bookedRanges.length > 0 && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Already booked {bookedDayLabel}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {bookedRanges.map((b) => (
                    <span
                      key={b.id}
                      title={b.title}
                      className="rounded border bg-background px-2 py-0.5 text-[11px] tabular-nums text-muted-foreground"
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Timezone display when lead is selected */}
            {effectiveLeadState && stateTimezones[effectiveLeadState] && selectedDate && form.watch('time') && (() => {
              const tzDisplay = getClientTimeDisplay(form.watch('time'), selectedDate);
              if (!tzDisplay || tzDisplay.sameTimezone) return null;
              return (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Clock className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="flex-1 text-sm">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span>
                        <span className="font-medium text-blue-900">Client:</span>{" "}
                        <span className="text-blue-700">{tzDisplay.clientTime}</span>{" "}
                        <span className="text-blue-500 text-xs">({tzDisplay.clientTz})</span>
                      </span>
                      <span className="text-gray-400">|</span>
                      <span>
                        <span className="font-medium text-gray-700">You:</span>{" "}
                        <span className="text-gray-600">{tzDisplay.agentTime}</span>{" "}
                        <span className="text-gray-500 text-xs">({tzDisplay.agentTz})</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <FormField
              control={control}
              name="sendEmailConf"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                      disabled={!hasEmailConnected || isCheckingEmailConnection}
                      className={cn(!hasEmailConnected && "opacity-50 cursor-not-allowed")}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className={cn(!hasEmailConnected && "text-muted-foreground")}>
                      Send Email Confirmation
                    </FormLabel>
                    {!hasEmailConnected && !isCheckingEmailConnection && (
                      <p className="text-xs text-muted-foreground">
                        Connect your Gmail or Outlook in{" "}
                        <Link href="/settings?tab=account&accountTab=connections" className="text-primary inline-flex items-center gap-1">
                          <Settings className="h-3 w-3" />
                          Settings
                        </Link>{" "}
                        to send email confirmations
                      </p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="apptNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-y"
                      placeholder="Additional notes about the appointment..."
                      rows={5}
                      style={{ minHeight: "120px" }}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="associations"
              render={() => (
                <FormItem>
                  <FormLabel>Lead</FormLabel>
                  <FormControl>
                    {(preselectedLeadId && preselectedLeadName) || (isEditing && selectedLeadId) ? (
                      <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
                        <span className="text-foreground">
                          {preselectedLeadName || form.watch('associations')?.[0]?.entityName || 'Selected Lead'}
                        </span>
                      </div>
                    ) : (
                      <LeadSelect
                        onValueChange={handleLeadSelect}
                        placeholder="Select a lead for this appointment..."
                        value={selectedLeadId}
                      />
                    )}
                  </FormControl>
                  {(preselectedLeadId || isEditing) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Lead cannot be changed for this appointment
                    </p>
                  )}
                </FormItem>
              )}
            />

            {isEditing && (
              <FormField
                control={control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={field.value || "scheduled"}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="rescheduled">Rescheduled</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="no_show">No Show</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button onClick={handleCancel} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={!isValid || isSubmitting} type="submit">
                {submitButtonText}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
        </React.Fragment>

  );
};